/**
 * G02: Coordinate Service
 * Transforms between WGS84, PL-2000, and Editor coordinate systems
 */

import proj4 from 'proj4'
import type { WGS84Coordinate, PL2000Coordinate, EditorCoordinate, GeoAnchor } from './types'

// Define EPSG:2180 (PL-2000 zone 7)
proj4.defs('EPSG:2180', '+proj=tmerc +lat_0=0 +lon_0=19 +k=0.9993 +x_0=500000 +y_0=-5300000 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs')

export class CoordinateService {
  private static instance: CoordinateService

  static getInstance(): CoordinateService {
    if (!CoordinateService.instance) {
      CoordinateService.instance = new CoordinateService()
    }
    return CoordinateService.instance
  }

  // ============ PL-2000 ↔ WGS84 ============

  /**
   * Convert PL-2000 to WGS84
   */
  pl2000ToWgs84(coord: PL2000Coordinate): WGS84Coordinate {
    const [lon, lat] = proj4('EPSG:2180', 'EPSG:4326', [coord.x, coord.y])
    return { lat, lon }
  }

  /**
   * Convert WGS84 to PL-2000
   */
  wgs84ToPl2000(coord: WGS84Coordinate): PL2000Coordinate {
    const [x, y] = proj4('EPSG:4326', 'EPSG:2180', [coord.lon, coord.lat])
    return { x, y }
  }

  // ============ Batch Conversions ============

  /**
   * Convert array of PL-2000 coordinates to WGS84
   */
  pl2000ArrayToWgs84(coords: PL2000Coordinate[]): WGS84Coordinate[] {
    return coords.map(c => this.pl2000ToWgs84(c))
  }

  /**
   * Convert array of WGS84 coordinates to PL-2000
   */
  wgs84ArrayToPl2000(coords: WGS84Coordinate[]): PL2000Coordinate[] {
    return coords.map(c => this.wgs84ToPl2000(c))
  }

  // ============ Editor Conversions ============

  /**
   * Convert WGS84 to Editor coordinates using geo-anchor
   */
  wgs84ToEditor(coord: WGS84Coordinate, anchor: GeoAnchor): EditorCoordinate {
    // Convert both to PL-2000 meters for accurate distance
    const coordPl = this.wgs84ToPl2000(coord)
    const anchorPl = this.wgs84ToPl2000(anchor.origin)

    // Calculate offset in meters
    const deltaX = coordPl.x - anchorPl.x
    const deltaY = coordPl.y - anchorPl.y

    // Convert to editor dots
    // Note: Editor Y is inverted (positive = down in 2D view)
    return {
      x: anchor.editorOrigin.x + (deltaX * anchor.dotsPerMeter),
      y: anchor.editorOrigin.y - (deltaY * anchor.dotsPerMeter)  // Y inverted
    }
  }

  /**
   * Convert Editor coordinates to WGS84 using geo-anchor
   */
  editorToWgs84(coord: EditorCoordinate, anchor: GeoAnchor): WGS84Coordinate {
    // Calculate offset in meters
    const deltaXMeters = (coord.x - anchor.editorOrigin.x) / anchor.dotsPerMeter
    const deltaYMeters = -(coord.y - anchor.editorOrigin.y) / anchor.dotsPerMeter  // Y inverted

    // Get anchor in PL-2000
    const anchorPl = this.wgs84ToPl2000(anchor.origin)

    // Apply offset
    const targetPl: PL2000Coordinate = {
      x: anchorPl.x + deltaXMeters,
      y: anchorPl.y + deltaYMeters
    }

    // Convert back to WGS84
    return this.pl2000ToWgs84(targetPl)
  }

  /**
   * Convert PL-2000 to Editor coordinates using geo-anchor (direct, no double projection)
   */
  pl2000ToEditor(coord: PL2000Coordinate, anchor: GeoAnchor): EditorCoordinate {
    const anchorPl = this.wgs84ToPl2000(anchor.origin)
    const deltaX = coord.x - anchorPl.x
    const deltaY = coord.y - anchorPl.y
    return {
      x: anchor.editorOrigin.x + (deltaX * anchor.dotsPerMeter),
      y: anchor.editorOrigin.y - (deltaY * anchor.dotsPerMeter)
    }
  }

  /**
   * Convert array of PL-2000 boundary coordinates to Editor coordinates
   */
  pl2000BoundaryToEditor(boundary: PL2000Coordinate[], anchor: GeoAnchor): EditorCoordinate[] {
    return boundary.map(c => this.pl2000ToEditor(c, anchor))
  }

  /**
   * Convert array of WGS84 boundary coordinates to Editor coordinates
   */
  wgs84BoundaryToEditor(boundary: WGS84Coordinate[], anchor: GeoAnchor): EditorCoordinate[] {
    return boundary.map(c => this.wgs84ToEditor(c, anchor))
  }

  /**
   * Convert array of Editor boundary coordinates to WGS84
   */
  editorBoundaryToWgs84(boundary: EditorCoordinate[], anchor: GeoAnchor): WGS84Coordinate[] {
    return boundary.map(c => this.editorToWgs84(c, anchor))
  }
}

// Export singleton instance
export const coordinateService = CoordinateService.getInstance()
